import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import "./User.css";
import { Button } from "primereact/button";
import SearchInput from "./SearchInput";

const UserTable = ({
  users,
  tableLoading,
  onEdit,
  onDelete,
  page,
  perPageSize,
  allUsers,
  search,
  setSearch,
}) => {
  /* =======================
     FETCH STATE
     ======================= */
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const startedFetchRef = useRef(false);

  useEffect(() => {
    if (tableLoading) startedFetchRef.current = true;
    if (!tableLoading && startedFetchRef.current) {
      setHasFetchedOnce(true);
    }
  }, [tableLoading]);

  const showSkeleton = tableLoading || !hasFetchedOnce;

  /* =======================
     SKELETON
     ======================= */
  const skeletonRows = useMemo(
    () =>
      Array.from({ length: perPageSize }, (_, i) => ({
        id: `skeleton-${i}`,
      })),
    [perPageSize]
  );

  const skeletonBody = () => <Skeleton width="100%" height="1.4rem" />;

  /* =======================
     HELPERS
     ======================= */
  const getUserNameById = (id) => {
    if (!id) return "-";
    const user = allUsers?.find((u) => u.id == id);
    return user ? user.name : "-";
  };

  /* =======================
     COLUMN BODY
     ======================= */
  const numberBody = (_, options) =>
    showSkeleton
      ? skeletonBody()
      : options.rowIndex + 1 + (page - 1) * perPageSize;

  const roleBody = (row) =>
    showSkeleton ? (
      skeletonBody()
    ) : (
      <div className="text-start">{row.role || "-"}</div>
    );

  const moBody = (row) =>
    showSkeleton ? skeletonBody() : getUserNameById(row.mo_id);

  const tlsBody = (row) =>
    showSkeleton ? skeletonBody() : getUserNameById(row.tls_id);

  const actionBody = (row) =>
    showSkeleton ? (
      skeletonBody()
    ) : (
      <React.Fragment>
        <Button
          icon="pi pi-pencil"
          className="action-btn edit-btn mr-2 rounded"
          rounded
          outlined
          onClick={() => onEdit(row)}
        />
        <Button
          icon="pi pi-trash"
          className="action-btn delete-btn rounded"
          rounded
          outlined
          onClick={() => onDelete(row.id)}
        />
      </React.Fragment>
    );

  const shouldShowEmpty = hasFetchedOnce && !tableLoading && users.length === 0;

  /* =======================
     RENDER
     ======================= */
  return (
    <div className="table-container">
      <DataTable
        value={showSkeleton ? skeletonRows : users}
        rowHover
        showGridlines
        responsiveLayout="scroll"
        className="p-datatable-sm"
        emptyMessage={shouldShowEmpty ? "Tidak ada data" : null}
      >
        <Column
          header="No"
          body={numberBody}
          style={{ width: "60px", textAlign: "center", cursor: "pointer" }}
        />

        <Column
          header="Nama"
          body={(row) => (showSkeleton ? skeletonBody() : row.name || "-")}
          style={{ width: "150px", cursor: "pointer" }}
        />

        <Column
          header="Email"
          body={(row) => (showSkeleton ? skeletonBody() : row.email || "-")}
          style={{ cursor: "pointer" }}
        />

        <Column header="Role" body={roleBody} style={{ cursor: "pointer" }} />

        <Column header="MO" body={moBody} style={{ cursor: "pointer" }} />

        <Column header="TLS" body={tlsBody} style={{ cursor: "pointer" }} />

        <Column
          header="Aksi"
          body={actionBody}
          style={{ minWidth: "150px", textAlign: "center", cursor: "pointer" }}
        />
      </DataTable>
    </div>
  );
};

export default UserTable;
